/**
 * @fileoverview ToggleGroup - Group of toggle buttons
 *
 * Purpose: Multiple toggle buttons in a group (single or multiple selection)
 * Dependencies: Radix UI ToggleGroup primitives, Toggle component
 * Used by: Toolbars, formatting bars, filter groups
 *
 * Components exported:
 * - ToggleGroup: Root toggle group container
 * - ToggleGroupItem: Individual toggle button
 *
 * Features:
 * - Single or multiple selection modes
 * - Inherits Toggle variants and sizes
 * - Keyboard navigation
 * - Accessible
 *
 * @example
 * <ToggleGroup type="single">
 *   <ToggleGroupItem value="left">Left</ToggleGroupItem>
 *   <ToggleGroupItem value="center">Center</ToggleGroupItem>
 *   <ToggleGroupItem value="right">Right</ToggleGroupItem>
 * </ToggleGroup>
 *
 * @example
 * // Multiple selection
 * <ToggleGroup type="multiple">
 *   <ToggleGroupItem value="bold"><Bold /></ToggleGroupItem>
 *   <ToggleGroupItem value="italic"><Italic /></ToggleGroupItem>
 *   <ToggleGroupItem value="underline"><Underline /></ToggleGroupItem>
 * </ToggleGroup>
 */

import * as React from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/misc";
import { toggleVariants } from "./toggle.variants";

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: "default",
  variant: "default",
});

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn("flex items-center justify-center gap-1", className)}
    {...props}
  >
    <ToggleGroupContext.Provider value={{ variant, size }}>
      {children}
    </ToggleGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
));

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
